import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Modal,
  notification,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'
import { useState } from 'react'
import {
  downloadGoldenPromptCsvTemplate,
  GoldenPromptUploadError,
  GoldenPromptUploadResponse,
  GOLDEN_PROMPT_UPLOAD_MAX_BYTES,
  GOLDEN_PROMPT_UPLOAD_MAX_ROWS,
  summarizeUploadResult,
} from './goldenPromptsAdminModel'
import { useUploadGoldenPromptsAdminMutation } from './goldenPromptsAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
}

const errorColumns = [
  { title: 'Row', dataIndex: 'row', width: 80 },
  {
    title: 'Prompt',
    dataIndex: 'prompt_text',
    render: (val: string) =>
      val ? (
        <Typography.Text ellipsis style={{ maxWidth: 320 }}>
          {val}
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
  { title: 'Reason', dataIndex: 'reason' },
]

const GoldenPromptUploadModal = ({ open, onClose }: Props) => {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<GoldenPromptUploadResponse | null>(null)
  const uploadMutation = useUploadGoldenPromptsAdminMutation()

  const uploadProps: UploadProps = {
    accept: '.csv,text/csv',
    multiple: false,
    maxCount: 1,
    fileList: file
      ? [{ uid: '-1', name: file.name, status: 'done', size: file.size } as UploadFile]
      : [],
    beforeUpload: (selected) => {
      if (selected.size > GOLDEN_PROMPT_UPLOAD_MAX_BYTES) {
        notification.error({
          message: 'File too large',
          description: `Maximum upload size is ${GOLDEN_PROMPT_UPLOAD_MAX_BYTES} bytes (1 MiB).`,
        })
        return Upload.LIST_IGNORE
      }
      setFile(selected)
      setResult(null)
      return false
    },
    onRemove: () => {
      setFile(null)
      return true
    },
  }

  const handleSubmit = () => {
    if (!file) {
      return
    }
    uploadMutation.mutate(file, {
      onSuccess: (response) => {
        setResult(response)
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : 'Upload failed'
        notification.error({ message: 'Golden prompts upload failed', description: message })
      },
    })
  }

  const handleUploadAnother = () => {
    setFile(null)
    setResult(null)
    uploadMutation.reset()
  }

  const submitting = uploadMutation.isLoading
  const showResults = result !== null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Upload Golden Prompts"
      width={720}
      destroyOnClose
      footer={
        showResults
          ? [
              <Button key="another" onClick={handleUploadAnother}>
                Upload another
              </Button>,
              <Button key="close" type="primary" onClick={onClose}>
                Close
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                disabled={!file}
                loading={submitting}
              >
                Upload
              </Button>,
            ]
      }
    >
      {showResults ? (
        <ResultsPanel result={result} />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="CSV format"
            description={
              <>
                Required column: <code>prompt_text</code>. Optional column:{' '}
                <code>prompt_type</code> (<code>normal</code> or <code>adversarial</code>;
                defaults to <code>normal</code>). Limits: 1 MiB and{' '}
                {GOLDEN_PROMPT_UPLOAD_MAX_ROWS} rows. All rows in this upload will be
                attributed to you as the author.
              </>
            }
          />
          <Button
            icon={<DownloadOutlined />}
            type="link"
            onClick={downloadGoldenPromptCsvTemplate}
            style={{ padding: 0 }}
          >
            Download CSV template
          </Button>
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag a CSV file here to select it
            </p>
            <p className="ant-upload-hint">Single .csv file, up to 1 MiB.</p>
          </Upload.Dragger>
        </Space>
      )}
    </Modal>
  )
}

type ResultsPanelProps = { result: GoldenPromptUploadResponse }

const ResultsPanel = ({ result }: ResultsPanelProps) => {
  const summary = summarizeUploadResult(result)
  const hasErrors = result.errors.length > 0
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type={hasErrors ? 'warning' : 'success'}
        showIcon
        message={summary}
        description={
          hasErrors ? (
            <span>
              <Tag color="volcano">{result.errors.length} errors</Tag> See per-row details
              below.
            </span>
          ) : undefined
        }
      />
      {hasErrors && (
        <Table<GoldenPromptUploadError>
          dataSource={result.errors}
          columns={errorColumns}
          rowKey={(row) => `${row.row}-${row.reason}`}
          size="small"
          pagination={result.errors.length > 10 ? { pageSize: 10 } : false}
        />
      )}
    </Space>
  )
}

export default GoldenPromptUploadModal
